// Recuperación tras un deploy — por qué el personalizador «se pilla»
// hasta que el cliente recarga la página.
//
// El bundle del embed se parte en chunks con hash de contenido
// (`embed-brandeate-customizer-<hash>.js`) que SOLO existen dentro de
// la imagen de Railway que los construyó. Cada deploy sustituye el
// contenedor: los chunks del build anterior desaparecen del disco y
// `/storefront/embed/:filename` empieza a responder 404 para ellos.
//
// La ficha de producto, en cambio, sobrevive al deploy. El móvil del
// cliente la mantiene viva en una pestaña (o la restaura de bfcache)
// con el `embed.js` viejo YA ejecutado en memoria — el `max-age=60`
// del entry no la toca, porque nadie vuelve a pedirlo. Cuando el
// cliente pulsa «Personalizar» horas más tarde, el `import()` dinámico
// pide un chunk que ya no está: el customizador no abre y lo único que
// lo arregla es recargar a mano.
//
// Verificado en producción el 25-ago-2026: a las 07:58 UTC un Android
// (SM-A505F) pidió cuatro chunks del build anterior —
// brandeate-customizer, shell-body, validation, first-run — y los
// cuatro dieron 404, mientras los chunks del build vivo se servían con
// 200 al resto de clientes. El deploy que los borró había entrado a las
// 23:00 de la víspera: NUEVE HORAS antes. No es una ventana de
// segundos, es toda la vida de la pestaña.
//
// Lo que hace este módulo: cuando un `import()` dinámico falla,
// pregunta al servidor por su id de build. Si no coincide con el que
// este bundle lleva grabado, la página es de una versión que ya no
// existe y se recarga UNA sola vez — justo lo que iba a hacer el
// cliente, salvo que la mayoría no lo hace: cierra la pestaña.
//
// Se compara el id de build en vez de sondear la URL del chunk que ha
// fallado a propósito: Safari no la incluye en el mensaje de error
// («Importing a module script failed.») y iOS es media tienda.

/** Sello de este build, inyectado por `vite.config.ts` vía `define`.
 *  El API sirve exactamente el mismo valor en
 *  `/storefront/embed/version`, leyéndolo del `dist/build-id.txt` que
 *  escribe ese mismo build. Fuera del bundle (tests con vitest) el
 *  identificador no existe: el `typeof` lo deja en cadena vacía y
 *  desactiva la recarga, que es la respuesta segura. */
declare const __EMBED_BUILD_ID__: string;
const BUILD_ID: string =
  typeof __EMBED_BUILD_ID__ === 'string' ? __EMBED_BUILD_ID__ : '';

/** Una sola recarga por pestaña y por build. La clave guarda el id que
 *  provocó la recarga, no un booleano: tras recargar, el bundle nuevo
 *  tiene otro id, así que un deploy posterior en la misma pestaña
 *  vuelve a poder recuperarse. Y si la recarga NO arregla nada (chunk
 *  realmente ausente del servidor, proxy que cachea el entry a lo
 *  bestia), el id sigue siendo el mismo y no entramos en bucle: el
 *  cliente ve el error, que es mejor que una página que parpadea. */
const RELOAD_GUARD_KEY = 'dfp:stale-bundle-reloaded';

/** Sondeo corto: si el API no contesta en 5 s no vamos a recargar de
 *  todas formas. Con AbortController a mano en vez de reusar
 *  `fetch-timeout.ts` para no meter una dependencia estática nueva en
 *  el entry — que se descarga en cada page-view de producto y tiene
 *  presupuesto de bytes (bundle-budget.test.ts). */
async function fetchJson(url: string, timeoutMs: number): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!res.ok) return null;
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

let _serverBuildId: Promise<string | null> | null = null;
let _entryRefrescado: Promise<void> | null = null;

/** Trae el entry de la RED antes de recargar.
 *
 *  `embed.js` se sirve con `max-age=60`, así que una recarga a secas
 *  puede volver a servirlo de la caché del navegador: el MISMO bundle,
 *  el mismo chunk que no existe, el mismo error — y ya sin segunda
 *  oportunidad, porque el guard de arriba dice que por este build ya se
 *  recargó una vez. Visto el 8-sep-2026 en la ficha de un producto tras
 *  varios despliegues seguidos: el cliente se quedó con el recuadro
 *  rojo puesto en vez de recargar solo.
 *
 *  `cache: 'reload'` obliga a ir a la red y deja la entrada de caché
 *  actualizada, así que la recarga que viene después ya arranca con el
 *  bundle nuevo. Memoizado y con tope de 3 s: si no se puede, se
 *  recarga igual — es lo que se hacía antes de esto. */
function refrescarEntry(): Promise<void> {
  if (!_entryRefrescado) {
    _entryRefrescado = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 3_000);
      try {
        await fetch(import.meta.url, {
          cache: 'reload',
          credentials: 'omit',
          signal: controller.signal,
        });
      } catch { /* red, CORS, entorno sin fetch: se recarga igual */ }
      finally { clearTimeout(timer); }
    })();
  }
  return _entryRefrescado;
}

/** Id de build que sirve el API AHORA. Memoizado: si fallan cuatro
 *  chunks a la vez (el caso real de producción) esto es una sola
 *  petición, no cuatro.
 *
 *  La URL sale de `import.meta.url` —la del propio `embed.js`— y no de
 *  `data-api`: así funciona igual venga el mount del bloque de sección,
 *  del loader del tema o del ScriptTag, que resuelven ese atributo de
 *  formas distintas. `null` = no se ha podido saber (API viejo sin la
 *  ruta, red caída, harness de dev): no se recarga. */
function serverBuildId(): Promise<string | null> {
  if (!_serverBuildId) {
    _serverBuildId = (async () => {
      try {
        const url = new URL('./version', import.meta.url).toString();
        const json = await fetchJson(url, 5_000) as { buildId?: unknown } | null;
        return typeof json?.buildId === 'string' ? json.buildId : null;
      } catch {
        return null;
      }
    })();
  }
  return _serverBuildId;
}

/** ¿El fallo de este `import()` es porque la página es de un build
 *  anterior al que hay desplegado? Si lo es, recarga (una vez) y
 *  resuelve `true`. Si no, resuelve `false` y quien llama enseña su
 *  error de siempre.
 *
 *  Exportada aparte de `lazyChunk` para que los tests puedan ejercitar
 *  la decisión sin montar un grafo de módulos. */
export async function recoverFromStaleBundle(err: unknown): Promise<boolean> {
  // eslint-disable-next-line no-console
  console.error('[personalizer-embed] fallo al cargar un chunk', err);
  if (typeof window === 'undefined') return false;
  // Sin red no hay versión nueva que traer: recargar solo cambiaría el
  // error del personalizador por la pantalla de sin-conexión del
  // navegador, y encima perdería la página.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  let guard: string | null = null;
  try { guard = window.sessionStorage.getItem(RELOAD_GUARD_KEY); }
  catch { return false; }               // modo incógnito bloqueado → no insistimos
  if (guard === BUILD_ID) return false; // ya recargamos por este build

  const server = await serverBuildId();
  if (!BUILD_ID || !server || server === BUILD_ID) return false;

  try { window.sessionStorage.setItem(RELOAD_GUARD_KEY, BUILD_ID); }
  catch { /* sin sessionStorage recargamos igual, pero solo esta vez */ }
  // console.warn sobrevive al build (index.ts documenta por qué): esto
  // tiene que verse en el móvil de un cliente si vuelve a pasar.
  console.warn(
    `[personalizer-embed] bundle obsoleto (${BUILD_ID} → ${server}) — recargando la página`,
  );
  // Que la recarga no pueda traer el mismo bundle de la caché.
  await refrescarEntry();
  window.location.reload();
  return true;
}

/** Envuelve un `import()` dinámico con memoización que NO se queda
 *  pegada al fallo, más la recuperación de arriba.
 *
 *  El memo de antes guardaba también los rechazos: una vez que el chunk
 *  fallaba, el segundo clic del cliente reusaba la promesa rota y
 *  fallaba al instante sin llegar a pedir nada. Aquí el fallo limpia el
 *  memo, así que un corte de red pasajero se arregla volviendo a
 *  pulsar. */
export function lazyChunk<T>(load: () => Promise<T>): () => Promise<T> {
  let inflight: Promise<T> | null = null;
  return () => {
    if (!inflight) {
      inflight = load().catch((err: unknown) => {
        inflight = null;
        void recoverFromStaleBundle(err);
        throw err;
      });
    }
    return inflight;
  };
}
